import React, { useContext, useState } from 'react';
import { useTranslations } from '@vp/om-translations';
import { Button, Span, VisuallyHidden } from '@vp/swan';
import PropTypes from 'prop-types';
import { reorderItemsWithSpecifiedQuantities } from '../../../apis/oreosApi';
import { getShopperId } from '../../../util/auth';
import {
  docRemediationModalPresentedToCustomer,
  trackBuyItAgainReorderProductAddedToCart,
  trackReorderProductAddedToCart,
} from '../../../apis/trackingApi';
import {
  trackDocumentConversionRemediationPresentedToCustomer,
  trackReorderUnavailableForProduct,
} from '../../../apis/newRelicTrackingApi';
import {
  BUY_IT_AGAIN_SOURCE_PAGE,
  REORDER_ACTION,
} from '../../../constants/orderdetailsui';
import useSiteData from '../../../hooks/useSiteData';
import { useLineItemData } from '../../contexts/LineItemDataProvider';
import { BasicPreloader } from '../BasicPreloader';
import { LineItemReorderActionStatusContext } from '../../contexts/LineItemReorderActionStatusContext';
import { BuyItAgainItemContext } from '../../orderhistory/buyItAgainTab/BuyItAgainItemContext';
import { SourcePageContext } from '../../contexts/SourcePageContext';
import { invalidateMiniCartData } from '../../../util/bookendsUtils';
import {
  IS_REORDERABLE_FALSE,
  isDocumentConversionFailure,
  isProductUnavailableForReorder,
} from '../../../util/reorderResponseHelpers';

export const ItemAddToCartButton = ({ sourcePageOverride, ...buttonProps }) => {
  const [orderDetailsMessages] = useTranslations(['orderDetailsMessages']);

  const {
    setAddToCartFailure,
    setAddToCartSuccess,
    setDocConversionFailure,
    setAltDocId,
    setProductUnavailableFailure,
    resetReorderActionFeedback,
  } = useContext(LineItemReorderActionStatusContext);

  const { sourcePage: defaultSourcePage } = useContext(SourcePageContext);
  const sourcePage = sourcePageOverride || defaultSourcePage;

  const item = useLineItemData();
  const {
    id,
    orderNumber,
    actions: { reorder: isReorderable },
    productName,
  } = item;
  const shopperId = getShopperId();
  const { siteName } = useSiteData();

  const { tenantEnvironment, vistacartTenant: vistacartTenantName } =
    useSiteData();

  const [awaitingApiResponse, setAwaitingApiResponse] = useState(false);

  // This component is rendered by both the BIA and non-BIA pages, where only BIA pages will render it inside a BuyItAgainContextProvider.
  // In the non-BIA scenario where BuyItAgainItemContext is not initialized, this hook call will safely return `undefined` values.
  const { selectedPricedQuantity } = useContext(BuyItAgainItemContext);

  // If this button is rendered on a BIA item, we must respect the quantity the shopper has selected in the dropdown
  // Otherwise, we can default to the existing quantity on the item
  const itemQuantityForReorder =
    sourcePage === BUY_IT_AGAIN_SOURCE_PAGE
      ? selectedPricedQuantity?.quantity
      : item.quantity;

  const handleProductUnavailable = reorderResponse => {
    setProductUnavailableFailure(true);

    const failureReason =
      reorderResponse?.results[0]?.failureReason || IS_REORDERABLE_FALSE;
    const analyticsMetadata = {
      sourcePage,
      shopperId,
      orderNumber,
      lineItemId: item.id,
      productKey: item.productKey,
      productVersion: item.productVersion,
      siteName,
      tenant: vistacartTenantName,
      action: REORDER_ACTION,
      failureReason,
    };
    trackReorderUnavailableForProduct(shopperId, analyticsMetadata);
  };

  const handleDocumentConversionFailure = reorderResponse => {
    const failedResult = reorderResponse?.results[0];
    // there was a doc conversion failure, lets pop that modal!
    setDocConversionFailure(true);
    setAltDocId(failedResult.altDocId);

    const analyticsMetadata = {
      sourcePage,
      shopperId,
      orderNumber,
      tenant: vistacartTenantName,
      siteName,
      item,
      action: REORDER_ACTION,
    };
    docRemediationModalPresentedToCustomer(analyticsMetadata);
    trackDocumentConversionRemediationPresentedToCustomer(
      shopperId,
      analyticsMetadata
    );
  };

  /**
   * Given a response from the reorder service, determine if that error was due to a document conversion failure or product unavailable failure
   * @param reorderResponse
   * @returns {Promise<void>}
   */
  const handleErrorResponse = async reorderResponse => {
    // does the response contain a lineItem result that failed due to doc issues?
    if (isDocumentConversionFailure(reorderResponse)) {
      handleDocumentConversionFailure(reorderResponse);
    } else if (isProductUnavailableForReorder(reorderResponse)) {
      handleProductUnavailable(reorderResponse);
    } else {
      setAddToCartFailure(true);
    }
  };

  const addToCartOnClickHandler = async () => {
    resetReorderActionFeedback(REORDER_ACTION);

    if (isReorderable) {
      setAwaitingApiResponse(true);
      try {
        const reorderItems = [
          {
            lineItemId: id,
            quantity: itemQuantityForReorder,
            analytics: {
              source: sourcePage,
            },
          },
        ];

        await reorderItemsWithSpecifiedQuantities({
          tenantEnvironment,
          orderNumber,
          reorderItems,
          vistacartTenantName,
          reorderSourcePage: sourcePage,
        });

        setAwaitingApiResponse(false);
        setAddToCartSuccess(true);
        invalidateMiniCartData({});

        // track reorder being added to cart successfully
        if (sourcePage === BUY_IT_AGAIN_SOURCE_PAGE) {
          trackBuyItAgainReorderProductAddedToCart({
            item,
            pricedQuantity: selectedPricedQuantity,
          });
        } else {
          trackReorderProductAddedToCart(item, sourcePage);
        }
      } catch (err) {
        setAwaitingApiResponse(false);
        await handleErrorResponse(err);
      }
    } else {
      handleProductUnavailable(null);
    }
  };

  return (
    <Button
      skin="primary"
      onClick={addToCartOnClickHandler}
      disabled={awaitingApiResponse}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...buttonProps}
    >
      {awaitingApiResponse && <BasicPreloader size="tiny" />}
      {/* This padding prevents the button from resizing when the preloader appears/disappears */}
      <Span mx={awaitingApiResponse ? 0 : 3}>
        {orderDetailsMessages.addToCartButtonText}
      </Span>
      <VisuallyHidden>{`: ${productName}`}</VisuallyHidden>
    </Button>
  );
};

ItemAddToCartButton.propTypes = {
  sourcePageOverride: PropTypes.string,
  buttonProps: PropTypes.any,
};
